import { 
    db, 
    firebase 
} from '../Config/firebase';
import { 
    collection, 
    getDocs, 
    getDoc,
    doc, 
    query, 
    where, 
    orderBy, 
    limit,
    addDoc,
    updateDoc,
    deleteDoc,
    writeBatch
} from 'firebase/firestore';
import { toast } from 'react-toastify';

// Puzzle interface
export interface ChessPuzzle {
    id: string;
    fen: string;
    moves: string;
    rating: number;
    themes: string[];
    description?: string;
    hint?: string;
    complexity: 'beginner' | 'intermediate' | 'advanced';
    category: 'tactics' | 'endgame' | 'opening' | 'middlegame';
    source: 'lichess' | 'custom' | 'local';
    lichessId?: string;
    createdAt?: any;
    createdBy?: string;
}

// Bookmark interface
export interface PuzzleBookmark {
    puzzleId: string;
    userId: string;
    subscriptionId: string;
    createdAt: any;
}

/**
 * Firestore Structure:
 * 
 * /serviceProviders/{providerId}/chessPuzzles/{puzzleId}
 * /serviceProviders/{providerId}/chessPuzzles/{puzzleId}/bookmarks/{userId}
 * 
 * This allows puzzles to be shared across all subscriptions of a service provider
 * 
 * Note: LiveKit integration uses the existing ChessMove interface in ChequeredChess:
 * - type: 'puzzle' - for sharing puzzles
 * - type: 'move' - for sharing moves
 * - type: 'position' - for sharing positions
 */

// Get all puzzles for a service provider
export const getPuzzlesForProvider = async (
    serviceProviderId: string,
    filters?: {
        complexity?: 'beginner' | 'intermediate' | 'advanced' | 'all';
        category?: 'tactics' | 'endgame' | 'opening' | 'middlegame' | 'all';
        searchQuery?: string;
        limit?: number;
    }
): Promise<ChessPuzzle[]> => {
    try {
        const puzzlesRef = collection(db, `serviceProviders/${serviceProviderId}/chessPuzzles`);
        
        let q = query(puzzlesRef);
        
        // Apply filters
        if (filters?.complexity && filters.complexity !== 'all') {
            q = query(q, where('complexity', '==', filters.complexity));
        }
        
        if (filters?.category && filters.category !== 'all') {
            q = query(q, where('category', '==', filters.category));
        }
        
        // Order by rating
        q = query(q, orderBy('rating', 'asc'));
        
        if (filters?.limit) {
            q = query(q, limit(filters.limit));
        }
        
        const snapshot = await getDocs(q);
        let puzzles = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as ChessPuzzle));
        
        // Client-side search filtering
        if (filters?.searchQuery) {
            const searchLower = filters.searchQuery.toLowerCase();
            puzzles = puzzles.filter(puzzle => 
                puzzle.description?.toLowerCase().includes(searchLower) ||
                puzzle.themes.some(theme => theme.toLowerCase().includes(searchLower))
            );
        }
        
        return puzzles;
    } catch (error) {
        console.error('Error fetching puzzles:', error);
        toast.error('Failed to load puzzles');
        return [];
    }
};

// Get bookmarked puzzles for a user in a subscription
export const getBookmarkedPuzzles = async (
    serviceProviderId: string,
    userId: string
): Promise<string[]> => {
    try {
        const puzzlesRef = collection(db, `serviceProviders/${serviceProviderId}/chessPuzzles`);
        const snapshot = await getDocs(puzzlesRef);
        
        const bookmarkedIds: string[] = [];
        
        for (const puzzleDoc of snapshot.docs) {
            const bookmarkRef = doc(db, `serviceProviders/${serviceProviderId}/chessPuzzles/${puzzleDoc.id}/bookmarks/${userId}`);
            const bookmarkSnap = await getDoc(bookmarkRef);
            
            if (bookmarkSnap.exists()) {
                bookmarkedIds.push(puzzleDoc.id);
            }
        }
        
        return bookmarkedIds;
    } catch (error) {
        console.error('Error fetching bookmarks:', error);
        return [];
    }
};

// Bookmark a puzzle
export const bookmarkPuzzle = async (
    serviceProviderId: string,
    puzzleId: string,
    userId: string,
    subscriptionId: string
): Promise<boolean> => {
    try {
        const bookmarkRef = doc(db, `serviceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}/bookmarks/${userId}`);
        
        await updateDoc(bookmarkRef, {
            puzzleId,
            userId,
            subscriptionId,
            createdAt: firebase.serverTimestamp()
        }).catch(async () => {
            // If document doesn't exist, create it
            await addDoc(collection(db, `serviceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}/bookmarks`), {
                puzzleId,
                userId,
                subscriptionId,
                createdAt: firebase.serverTimestamp()
            });
        });
        
        toast.success('Puzzle bookmarked!');
        return true;
    } catch (error) {
        console.error('Error bookmarking puzzle:', error);
        toast.error('Failed to bookmark puzzle');
        return false;
    }
};

// Remove bookmark
export const removeBookmark = async (
    serviceProviderId: string,
    puzzleId: string,
    userId: string
): Promise<boolean> => {
    try {
        const bookmarkRef = doc(db, `serviceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}/bookmarks/${userId}`);
        await deleteDoc(bookmarkRef);
        
        toast.success('Bookmark removed');
        return true;
    } catch (error) {
        console.error('Error removing bookmark:', error);
        toast.error('Failed to remove bookmark');
        return false;
    }
};

// Add a single puzzle
export const addPuzzle = async (
    serviceProviderId: string,
    puzzle: Omit<ChessPuzzle, 'id' | 'createdAt'>,
    userId: string
): Promise<string | null> => {
    try {
        const puzzlesRef = collection(db, `serviceProviders/${serviceProviderId}/chessPuzzles`);
        
        const docRef = await addDoc(puzzlesRef, {
            ...puzzle,
            createdAt: firebase.serverTimestamp(),
            createdBy: userId
        });
        
        toast.success('Puzzle added successfully!');
        return docRef.id;
    } catch (error) {
        console.error('Error adding puzzle:', error);
        toast.error('Failed to add puzzle');
        return null;
    }
};

// Bulk import puzzles (for initial seeding or Lichess import)
export const bulkImportPuzzles = async (
    serviceProviderId: string,
    puzzles: Omit<ChessPuzzle, 'id' | 'createdAt'>[],
    userId: string
): Promise<number> => {
    try {
        const batch = writeBatch(db);
        const puzzlesRef = collection(db, `serviceProviders/${serviceProviderId}/chessPuzzles`);
        
        let count = 0;
        
        for (const puzzle of puzzles) {
            const docRef = doc(puzzlesRef);
            batch.set(docRef, {
                ...puzzle,
                createdAt: firebase.serverTimestamp(),
                createdBy: userId
            });
            count++;
            
            // Firestore batch limit is 500
            if (count % 500 === 0) {
                await batch.commit();
                console.log(`Imported ${count} puzzles...`);
            }
        }
        
        // Commit remaining
        if (count % 500 !== 0) {
            await batch.commit();
        }
        
        toast.success(`Successfully imported ${count} puzzles!`);
        return count;
    } catch (error) {
        console.error('Error bulk importing puzzles:', error);
        toast.error('Failed to import puzzles');
        return 0;
    }
};

// Import from Lichess
export const importFromLichess = async (
    serviceProviderId: string,
    lichessId: string,
    userId: string
): Promise<ChessPuzzle | null> => {
    try {
        const response = await fetch(`https://lichess.org/api/puzzle/${lichessId}`);
        
        if (!response.ok) {
            throw new Error('Failed to fetch puzzle from Lichess');
        }
        
        const data = await response.json();
        
        // Convert Lichess puzzle to our format
        const puzzle: Omit<ChessPuzzle, 'id' | 'createdAt'> = {
            fen: data.puzzle.initialPly || data.game.fen,
            moves: data.puzzle.solution.join(' '),
            rating: data.puzzle.rating,
            themes: data.puzzle.themes || [],
            description: `Lichess Puzzle #${data.puzzle.id}`,
            hint: `Themes: ${data.puzzle.themes?.join(', ')}`,
            complexity: data.puzzle.rating < 1500 ? 'beginner' : data.puzzle.rating < 2000 ? 'intermediate' : 'advanced',
            category: determineCategory(data.puzzle.themes),
            source: 'lichess',
            lichessId: lichessId
        };
        
        const puzzleId = await addPuzzle(serviceProviderId, puzzle, userId);
        
        if (puzzleId) {
            return { ...puzzle, id: puzzleId };
        }
        
        return null;
    } catch (error) {
        console.error('Error importing from Lichess:', error);
        toast.error('Failed to import puzzle from Lichess');
        return null;
    }
};

// Helper to determine category from themes
const determineCategory = (themes: string[]): 'tactics' | 'endgame' | 'opening' | 'middlegame' => {
    if (themes.some(t => t.includes('endgame'))) return 'endgame';
    if (themes.some(t => t.includes('opening'))) return 'opening';
    if (themes.some(t => ['middlegame', 'advantage'].includes(t))) return 'middlegame';
    return 'tactics';
};

// Delete a puzzle
export const deletePuzzle = async (
    serviceProviderId: string,
    puzzleId: string
): Promise<boolean> => {
    try {
        const puzzleRef = doc(db, `serviceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}`);
        await deleteDoc(puzzleRef);
        
        toast.success('Puzzle deleted');
        return true;
    } catch (error) {
        console.error('Error deleting puzzle:', error);
        toast.error('Failed to delete puzzle');
        return false;
    }
};

// Update a puzzle
export const updatePuzzle = async (
    serviceProviderId: string,
    puzzleId: string,
    updates: Partial<ChessPuzzle>
): Promise<boolean> => {
    try {
        const puzzleRef = doc(db, `serviceProviders/${serviceProviderId}/chessPuzzles/${puzzleId}`);
        await updateDoc(puzzleRef, updates);
        
        toast.success('Puzzle updated');
        return true;
    } catch (error) {
        console.error('Error updating puzzle:', error);
        toast.error('Failed to update puzzle');
        return false;
    }
};

// ========================
// LIVEKIT MEETING INTEGRATION NOTES
// ========================

/**
 * Meeting integration is already handled in ChequeredChess component using sendChessData()
 * 
 * To share a puzzle in a meeting:
 * 
 * 1. Load puzzle from Firestore (use getPuzzlesForProvider or getBookmarkedPuzzles)
 * 2. Use existing sendChessData() function in ChequeredChess:
 * 
 *    sendChessData({
 *        type: 'puzzle',
 *        from: '',
 *        to: '',
 *        san: '',
 *        fen: puzzle.fen,
 *        playerName: participantName,
 *        timestamp: Date.now(),
 *        puzzleId: puzzle.id
 *    });
 * 
 * 3. Students receive via existing dataReceived handler
 * 
 * This integrates seamlessly with the existing LiveKit infrastructure.
 */

